import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TrendingUp, TrendingDown, CheckCircle2, Trophy } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useClaimableWinnings } from "@/hooks/useClaimableWinnings";
import { useToast } from "@/hooks/use-toast";
import { useMarketTrading } from "@/hooks/useMarketTrading";
import { getContractAddresses } from "@/lib/contracts";
import { useState } from "react";
import { ERROR_MESSAGES, formatErrorMessage, logError } from "@/lib/errorMessages";

const TradingPositions = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { claimablePositions, totalClaimable, isLoading, hasClaimable } = useClaimableWinnings();
  const { executeTrade, getTradeEstimate, checkUniswapPool } = useMarketTrading();
  const [claimingPosition, setClaimingPosition] = useState<string | null>(null);
  const contracts = getContractAddresses();

  const handleClaimWinnings = async (presaleId: string, tokenAddress: string, amount: number) => {
    try {
      setClaimingPosition(presaleId);
      
      // Check if Uniswap pool exists
      const poolExists = await checkUniswapPool(tokenAddress, contracts.USDC);
      
      if (!poolExists) {
        toast({
          title: "No Liquidity Pool",
          description: "This market doesn't have a Uniswap pool yet. You can hold your winning tokens or wait for liquidity.",
          variant: "destructive"
        });
        setClaimingPosition(null);
        return;
      }

      // Get trade estimate
      const estimate = await getTradeEstimate(
        amount.toString(),
        presaleId,
        tokenAddress,
        contracts.USDC,
        'Graduated'
      );

      if (!estimate) {
        toast({
          title: "Cannot Estimate Trade",
          description: "Unable to get trade estimate. The market may not have enough liquidity.",
          variant: "destructive"
        });
        setClaimingPosition(null);
        return;
      }

      toast({
        title: "Swapping Winning Tokens",
        description: `Exchanging ${amount.toFixed(2)} tokens for ~$${estimate.amountOutFormatted.toFixed(2)} USDT`,
      });

      // Execute the trade (swap winning tokens for USDC)
      const success = await executeTrade(
        presaleId,
        amount.toString(),
        tokenAddress,
        contracts.USDC,
        estimate,
        'Graduated'
      );

      if (success) {
        toast({
          title: "Winnings Claimed!",
          description: `Successfully claimed ~$${estimate.amountOutFormatted.toFixed(2)} USDC`,
        });
      }
      
      setClaimingPosition(null);
    } catch (error: any) {
      logError('TradingPositions.handleClaimWinnings', error, { presaleId, tokenAddress, amount });
      toast({
        title: "Claim Failed",
        description: formatErrorMessage(error, ERROR_MESSAGES.TRADING.TRADE_FAILED),
        variant: "destructive"
      });
      setClaimingPosition(null);
    }
  };

  // Filter positions into open (not yet redeemable) and history (already claimed/redeemable)
  const positions = claimablePositions;
  const history: any[] = [];

  return (
    <div>
      <h2 className="text-xl font-semibold text-foreground mb-4">Trading Positions</h2>
      <Tabs defaultValue="open" className="space-y-6">
      <TabsList>
        <TabsTrigger value="open">Open Positions</TabsTrigger>
        <TabsTrigger value="history">History</TabsTrigger>
      </TabsList>

      {/* Claimable Winnings Summary */}
      {hasClaimable && (
        <Card className="p-6 bg-success/10 border-success/20 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Trophy className="w-12 h-12 text-success" />
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Claimable Winnings</p>
                <p className="text-3xl font-bold text-success">${totalClaimable.toFixed(2)}</p>
                <p className="text-xs text-muted-foreground mt-1">From {positions.length} winning position{positions.length > 1 ? 's' : ''}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground mb-2">Trade your winning tokens on Uniswap</p>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/')}
              >
                Browse Markets
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Open Positions */}
      <TabsContent value="open" className="space-y-4">
        {isLoading ? (
          <Card className="p-12 text-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading positions...</p>
          </Card>
        ) : positions.length === 0 ? (
          <Card className="p-12 text-center">
            <TrendingUp className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">
              No Claimable Positions
            </h3>
            <p className="text-muted-foreground mb-6">
              You don't have any winning positions to claim yet.
            </p>
            <Button variant="outline" onClick={() => navigate('/')}>
              Browse Markets
            </Button>
          </Card>
        ) : (
          positions.map((position) => (
          <Card key={position.presaleId} className="p-6 bg-success/5 border-success/30 border-border hover:border-success/50 transition-all">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {position.question}
                </h3>
                <div className="flex gap-2">
                  <Badge className={position.outcome === "YES" ? "bg-success/20 text-success" : "bg-destructive/20 text-destructive"}>
                    {position.outcome}
                  </Badge>
                  <Badge variant="outline">{position.balanceFormatted.toFixed(2)} tokens</Badge>
                  <Badge className="bg-success/20 text-success border-success/30">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Won - Tradeable
                  </Badge>
                </div>
              </div>
              <Button 
                className="bg-success hover:bg-success/90 text-white" 
                size="sm"
                onClick={() => handleClaimWinnings(position.presaleId, position.tokenAddress, position.balanceFormatted)}
                disabled={claimingPosition === position.presaleId}
              >
                {claimingPosition === position.presaleId ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Claiming...
                  </>
                ) : (
                  <>
                    <Trophy className="w-4 h-4 mr-2" />
                    Claim Winnings
                  </>
                )}
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground mb-1">Winning Tokens</p>
                <p className="font-medium text-foreground">{position.balanceFormatted.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-muted-foreground mb-1">Token Address</p>
                <p className="font-mono text-xs text-foreground">{position.tokenAddress.slice(0, 6)}...{position.tokenAddress.slice(-4)}</p>
              </div>
              <div>
                <p className="text-muted-foreground mb-1">Estimated Value</p>
                <p className="font-bold text-success">
                  ~${position.balanceFormatted.toFixed(2)}
                </p>
              </div>
            </div>
          </Card>
        ))
        )}
      </TabsContent>

      {/* History */}
      <TabsContent value="history" className="space-y-4">
        {history.length === 0 ? (
          <Card className="p-12 text-center">
            <CheckCircle2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">
              No Trading History
            </h3>
            <p className="text-muted-foreground">
              Your resolved positions will appear here.
            </p>
          </Card>
        ) : (
          history.map((item) => (
          <Card key={item.id} className="p-6 bg-card border-border">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {item.question}
                </h3>
                <div className="flex gap-2">
                  <Badge className={item.outcome === "YES" ? "bg-success/20 text-success" : "bg-destructive/20 text-destructive"}>
                    {item.outcome}
                  </Badge>
                  <Badge variant="outline">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Resolved
                  </Badge>
                  <Badge className={item.result === "won" ? "bg-success/20 text-success" : "bg-destructive/20 text-destructive"}>
                    {item.result === "won" ? "Won" : "Lost"}
                  </Badge>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground mb-1">Invested</p>
                <p className="font-medium text-foreground">${item.invested}</p>
              </div>
              <div>
                <p className="text-muted-foreground mb-1">Returned</p>
                <p className="font-medium text-foreground">${item.returned}</p>
              </div>
              <div>
                <p className="text-muted-foreground mb-1">Shares</p>
                <p className="font-medium text-foreground">{item.shares}</p>
              </div>
              <div>
                <p className="text-muted-foreground mb-1">P&L</p>
                <p className={`font-bold ${item.pnl >= 0 ? "text-success" : "text-destructive"}`}>
                  {item.pnl >= 0 ? "+" : ""}${item.pnl}
                </p>
              </div>
            </div>
          </Card>
        ))
        )}
      </TabsContent>
    </Tabs>
    </div>
  );
};

export default TradingPositions;